import axios from 'axios';
import { getApiBaseUrl, setupAxiosAuth } from '../utils/urlConfig';

const API_BASE_URL = `${getApiBaseUrl()}/admin`;

// Helper to ensure auth token is included in requests
const setupRequest = () => {
  setupAxiosAuth(axios);
};

/**
 * Fetches all batches with pagination and search
 */
export const fetchBatches = async (page = 1, limit = 10, search = '', group = undefined) => {
  try {
    setupRequest();
    const params = { page, limit, search };
    if (group !== undefined && group !== '') params.group = group;
    const response = await axios.get(`${API_BASE_URL}/batches`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching batches:', error);
    throw error;
  }
};

/**
 * Fetches students in a specific batch
 */
export const fetchBatchStudents = async (batchId, page = 1, limit = 10) => {
  try {
    setupRequest();
    const response = await axios.get(`${API_BASE_URL}/batches/${batchId}/students`, {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching students for batch ${batchId}:`, error);
    throw error;
  }
};

/**
 * Fetches all courses with pagination and search
 */
export const fetchCourses = async (page = 1, limit = 10, search = '') => {
  try {
    setupRequest();
    const response = await axios.get(`${API_BASE_URL}/courses`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching courses:', error);
    throw error;
  }
};

/**
 * Fetches chapters for a specific course
 */
export const fetchCourseChapters = async (courseId, page = 1, limit = 10) => {
  try {
    setupRequest();
    const response = await axios.get(`${API_BASE_URL}/courses/${courseId}/chapters`, {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching chapters for course ${courseId}:`, error);
    throw error;
  }
};

/**
 * Fetches all chapters with pagination and search
 */
export const fetchChapters = async (page = 1, limit = 10, search = '', courseId = '') => {
  try {
    setupRequest();
    const response = await axios.get(`${API_BASE_URL}/chapters`, {
      params: { page, limit, search, courseId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching chapters:', error);
    throw error;
  }
};

/**
 * Fetches content for a specific chapter
 */
export const fetchChapterContent = async (chapterId, page = 1, limit = 10) => {
  try {
    setupRequest();
    const response = await axios.get(`${API_BASE_URL}/chapters/${chapterId}/content`, {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching content for chapter ${chapterId}:`, error);
    throw error;
  }
};

/**
 * Triggers a Testpress content sync for one chapter and its immediate children.
 *
 * The server holds the request open for the whole sync, which is rate-limited
 * and can run for minutes on a large chapter, so this call overrides the default
 * axios timeout.
 */
const CHAPTER_CONTENT_SYNC_TIMEOUT_MS = 10 * 60 * 1000;

export const syncChapterContent = async (chapterId) => {
  try {
    setupRequest();
    const response = await axios.post(
      `${API_BASE_URL}/chapters/${chapterId}/sync-content`,
      {},
      { timeout: CHAPTER_CONTENT_SYNC_TIMEOUT_MS }
    );
    return response.data;
  } catch (error) {
    console.error(`Error syncing content for chapter ${chapterId}:`, error);
    throw error;
  }
};

/**
 * Fetches attempts for a specific chapter content
 */
export const fetchContentAttempts = async (contentId, page = 1, limit = 10) => {
  try {
    setupRequest();
    const response = await axios.get(`${API_BASE_URL}/content/${contentId}/attempts`, {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching attempts for content ${contentId}:`, error);
    throw error;
  }
};

/**
 * Fetches all exams with pagination and search
 */
export const fetchExams = async (page = 1, limit = 10, search = '') => {
  try {
    setupRequest();
    const response = await axios.get(`${API_BASE_URL}/exams`, {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching exams:', error);
    throw error;
  }
};

/**
 * Fetches attempts for a specific exam
 */
export const fetchExamAttempts = async (examId, page = 1, limit = 10) => {
  try {
    setupRequest();
    const response = await axios.get(`${API_BASE_URL}/exams/${examId}/attempts`, {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching attempts for exam ${examId}:`, error);
    throw error;
  }
};
/**
 * Fetch one batch's month calendar (schedules grouped by date, with flags).
 * @param {number} batchId
 * @param {string} month - 'YYYY-MM'
 */
export const fetchBatchCalendar = async (batchId, month, modes = []) => {
  try {
    setupRequest();
    const params = { month };
    if (Array.isArray(modes) && modes.length) params.mode = modes.join(',');
    const response = await axios.get(`${API_BASE_URL}/batches/${batchId}/calendar`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching batch calendar:', error);
    throw error;
  }
};

/**
 * Fetch a month calendar for one or more batches (e.g. a whole batch group).
 * @param {number[]} batchIds
 * @param {string} month - 'YYYY-MM'
 */
export const fetchGroupCalendar = async (batchIds, month, modes = []) => {
  try {
    setupRequest();
    const params = { month, batchIds: batchIds.join(',') };
    if (Array.isArray(modes) && modes.length) params.mode = modes.join(',');
    const response = await axios.get(`${API_BASE_URL}/calendar`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching group calendar:', error);
    throw error;
  }
};

/**
 * Create/upsert class schedules for one or more batches.
 * @param {{batchIds:number[], board:string, stream:number, sessions:object[]}} payload
 */
export const buildBatchSchedules = async (payload) => {
  try {
    setupRequest();
    const response = await axios.post(`${API_BASE_URL}/schedules/batch-build`, payload);
    return response.data;
  } catch (error) {
    console.error('Error building batch schedules:', error);
    throw error;
  }
};

/**
 * Fetch faculty (id + name) for pickers.
 */
export const fetchFaculty = async () => {
  try {
    setupRequest();
    const response = await axios.get(`${API_BASE_URL}/faculty`);
    return response.data;
  } catch (error) {
    console.error('Error fetching faculty:', error);
    throw error;
  }
};

/**
 * Fetch batches folded into the groups a schedule is authored against.
 * @param {boolean} includeInactive
 */
export const fetchBatchGroups = async (includeInactive = false, splitLiveGroups = false) => {
  try {
    setupRequest();
    const params = {};
    if (includeInactive) params.includeInactive = 'true';
    // The Month Planner plans one live group at a time and asks for the list
    // already split; the calendar picker wants whole groups.
    if (splitLiveGroups) params.splitLiveGroups = 'true';
    const response = await axios.get(`${API_BASE_URL}/batch-groups`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching batch groups:', error);
    throw error;
  }
};

/**
 * Fetch a course's chapters as the subject → chapter → topic tree they are,
 * excluding soft-deleted rows and without the flat endpoint's page cap.
 * @param {number} courseId
 */
export const fetchCourseChapterTree = async (courseId) => {
  try {
    setupRequest();
    const response = await axios.get(`${API_BASE_URL}/courses/${courseId}/chapter-tree`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching chapter tree for course ${courseId}:`, error);
    throw error;
  }
};

/**
 * Fetch a chapter's contents in teaching order, marking what the given batches
 * have already been scheduled.
 * @param {number} chapterId
 * @param {number[]} batchIds
 * @param {boolean} includeDescendants include the contents of every chapter
 *   below this one — a subject or chapter mostly groups its children rather
 *   than holding contents itself.
 * @param {string|null} mode restrict to the content types a session of this
 *   mode may be linked to (`Live` → VideoConference, `Exam`/`Unit Test` → Exam).
 */
export const fetchChapterContents = async (
  chapterId,
  batchIds = [],
  includeDescendants = false,
  mode = null,
) => {
  try {
    setupRequest();
    const params = {};
    if (Array.isArray(batchIds) && batchIds.length) params.batchIds = batchIds.join(',');
    if (includeDescendants) params.includeDescendants = 'true';
    // A Live session is taught from a video conference, an exam from an exam;
    // the server drops the rest rather than offering a link that would be
    // flagged the moment it is made.
    if (mode) params.mode = mode;
    const response = await axios.get(`${API_BASE_URL}/chapters/${chapterId}/contents`, { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching contents for chapter ${chapterId}:`, error);
    throw error;
  }
};

/**
 * Delete one class. Pass `dryRun` to learn what it would remove without
 * removing it — the confirmation dialog does this first, so it can name the
 * damage before the operator commits.
 * @param {number} scheduleId
 * @param {boolean} dryRun
 */
export const deleteSchedule = async (scheduleId, dryRun = false) => {
  try {
    setupRequest();
    const response = await axios.delete(`${API_BASE_URL}/schedules/${scheduleId}`, {
      params: dryRun ? { dryRun: 'true' } : {},
    });
    return response.data;
  } catch (error) {
    console.error(`Error deleting schedule ${scheduleId}:`, error);
    throw error;
  }
};

/**
 * Create one class on one date for a group of batches — the calendar's
 * "add a session". `contentIds` may be empty: a Live class or an exam is
 * routinely scheduled before the content it will point at exists.
 * @param {object} payload
 */
export const createScheduleSession = async (payload) => {
  try {
    setupRequest();
    const response = await axios.post(`${API_BASE_URL}/schedules/session`, payload);
    return response.data;
  } catch (error) {
    console.error('Error creating schedule session:', error);
    throw error;
  }
};

/**
 * Plan a month of classes. Dry run by default — pass dryRun:false to write.
 * @param {object} payload
 */
export const buildMonthSchedules = async (payload) => {
  try {
    setupRequest();
    const response = await axios.post(`${API_BASE_URL}/schedules/month-build`, payload);
    return response.data;
  } catch (error) {
    console.error('Error building month schedules:', error);
    throw error;
  }
};

/**
 * Move a class to another date in place, keeping its contents and history.
 * @param {number} scheduleId
 * @param {string} date - 'YYYY-MM-DD'
 */
export const moveSchedule = async (scheduleId, date) => {
  try {
    setupRequest();
    const response = await axios.patch(`${API_BASE_URL}/schedules/${scheduleId}/date`, { date });
    return response.data;
  } catch (error) {
    console.error(`Error moving schedule ${scheduleId}:`, error);
    throw error;
  }
};

/**
 * Repair schedules pointing at the wrong exam or video conference.
 * Dry run by default — pass dryRun:false to write.
 * @param {{from:string, to:string, batchId?:number, scope?:'all'|'exam'|'vc', dryRun?:boolean}} payload
 */
export const healScheduleLinks = async (payload) => {
  try {
    setupRequest();
    const response = await axios.post(`${API_BASE_URL}/schedules/heal`, payload);
    return response.data;
  } catch (error) {
    console.error('Error healing schedule links:', error);
    throw error;
  }
};

/**
 * Link content to one or more schedules (e.g. all batches in a group session).
 * @param {{scheduleIds:number[], contentIds:number[], chapterId?:number}} payload
 */
export const linkScheduleContents = async (payload) => {
  try {
    setupRequest();
    const response = await axios.patch(`${API_BASE_URL}/schedules/link-contents`, payload);
    return response.data;
  } catch (error) {
    console.error('Error linking schedule contents:', error);
    throw error;
  }
};
